import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import io from "socket.io-client";
import { socketUrl, socketsEnabled } from "../config/environment";
import { getCachedData, setCachedData } from "../services/dataCache";

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider",
    );
  }
  return context;
};

export const NotificationProvider = ({ children, navigate }) => {
  const [notifications, setNotifications] = useState([]);
  const [pendingCardAction, setPendingCardAction] = useState(null);

  const socketRef = useRef(null);
  const beneficiaryHandlersRef = useRef([]);
  const cardHandlersRef = useRef([]);
  const itemHandlersRef = useRef([]);

  // Function to decode JWT token and extract tenant from Alias claim
  const getTenantFromToken = useCallback((token) => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.Alias;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  }, []);

  // Function to play notification sound
  const playNotificationSound = useCallback(() => {
    try {
      // Create a simple ding sound using Web Audio API
      const audioContext = new (
        window.AudioContext || window.webkitAudioContext
      )();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Create a pleasant ding sound (800Hz for 500ms)
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.type = "sine";

      // Fade in and out for a smooth sound
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(
        0.8,
        audioContext.currentTime + 0.01,
      );
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        audioContext.currentTime + 0.5,
      );

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.warn("Could not play notification sound:", error);
    }
  }, []);

  const addNotification = useCallback(
    (notification) => {
      // Play notification sound
      playNotificationSound();

      setNotifications((prev) => [
        ...prev,
        {
          ...notification,
          id: Date.now(),
          timestamp: new Date(),
        },
      ]);
    },
    [playNotificationSound],
  );

  useEffect(() => {
    if (!socketsEnabled) {
      socketRef.current = null;
      console.log("Websocket connection disabled by REACT_APP_ENABLE_SOCKETS");
      return;
    }

    console.log("Connecting to websocket at:", socketUrl); // Debug log

    // Connect to WebSocket server
    const newSocket = io(socketUrl);
    socketRef.current = newSocket;

    newSocket.on("connect", () => {
      console.log("Websocket connected with ID:", newSocket.id); // Debug log
    });

    newSocket.on("disconnect", () => {
      console.log("Websocket disconnected"); // Debug log
    });

    newSocket.on("error", (error) => {
      console.error("Websocket error:", error); // Debug log
    });

    // Listen for real-time notifications
    newSocket.on("notification", (notification) => {
      // Play notification sound
      playNotificationSound();

      setNotifications((prev) => [
        ...prev,
        {
          ...notification,
          id: Date.now(),
          timestamp: new Date(),
        },
      ]);
    });

    // Add specific listener for beneficiaryAdded events
    newSocket.on("beneficiaryAdded", (beneficiaryData) => {
      console.log(
        "NotificationContext received beneficiaryAdded:",
        beneficiaryData,
      );

      // Update cached beneficiaries data globally
      const currentBeneficiaries = getCachedData("beneficiaries") || [];
      const exists = currentBeneficiaries.some(
        (b) => b.id === beneficiaryData.id,
      );

      if (!exists) {
        const updatedBeneficiaries = [...currentBeneficiaries, beneficiaryData];
        setCachedData("beneficiaries", updatedBeneficiaries);
        console.log("Updated cached beneficiaries:", updatedBeneficiaries);
      }

      // Add notification
      addNotification({
        type: "success",
        title: "Beneficiary Added",
        message: `${beneficiaryData.name} has been added successfully`,
      });

      // Call all registered beneficiary handlers (for components that need real-time updates)
      beneficiaryHandlersRef.current.forEach((handler) => {
        try {
          handler(beneficiaryData);
        } catch (error) {
          console.error("Error in beneficiaryAdded handler:", error);
        }
      });
    });

    // Add specific listener for itemAdded events
    newSocket.on("itemAdded", (itemData) => {
      console.log("NotificationContext received itemAdded:", itemData);

      // Update cached items data globally
      const currentItems = getCachedData("crud-items") || [];
      const exists = currentItems.some((item) => item.id === itemData.id);

      if (!exists) {
        const updatedItems = [...currentItems, itemData];
        setCachedData("crud-items", updatedItems);
        console.log("Updated cached items:", updatedItems);
      }

      // Add notification
      addNotification({
        type: "success",
        title: "Item Added",
        message: `${itemData.name} has been added successfully`,
      });

      // Call all registered item handlers (for components that need real-time updates)
      itemHandlersRef.current.forEach((handler) => {
        try {
          handler(itemData);
        } catch (error) {
          console.error("Error in itemAdded handler:", error);
        }
      });
    });

    // Add specific listener for cardAction events
    newSocket.on("cardAction", (cardData) => {
      // Add notification for card status change
      const actionText =
        cardData.action === "Block" ? "blocking" : "unblocking";
      addNotification({
        type: "success",
        title: `Card ${actionText} request is pending`,
        message: `A request has been initiated`,
      });

      // Set the pending card action to be processed when Cards component loads
      setPendingCardAction(cardData);

      // Navigate to My Cards screen if navigate function is available
      if (navigate) {
        navigate("/cards");
      }
    });

    // Add specific listener for startVoice events (disabled for mall app)
    // newSocket.on("startVoice", (voiceData) => {
    //   console.log("NotificationContext received startVoice:", voiceData);
    //   // Voice functionality removed for mall app
    // });

    return () => {
      newSocket.close();
      socketRef.current = null;
    };
  }, [addNotification, playNotificationSound, navigate, socketsEnabled]);

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((notif) => notif.id !== id));
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  const emitBeneficiaryAdd = (beneficiaryData) => {
    if (socketRef.current) {
      socketRef.current.emit("addBeneficiary", beneficiaryData);

      // Add local notification immediately
      addNotification({
        type: "success",
        title: "Beneficiary Added",
        message: `${beneficiaryData.name} has been added successfully`,
      });
    }
  };

  const emitItemAdd = (itemData) => {
    if (socketRef.current) {
      socketRef.current.emit("addItem", itemData);

      // Add local notification immediately
      addNotification({
        type: "success",
        title: "Item Added",
        message: `${itemData.name} has been added successfully`,
      });
    }
  };

  const emitCardStatusChange = (cardData, action) => {
    if (socketRef.current) {
      const eventData = {
        ...cardData,
        cardId: cardData.id, // Ensure cardId is set
        status: action === "block" ? "Blocked" : "Active",
        action: action,
      };

      console.log("Emitting changeCardStatus:", eventData);
      socketRef.current.emit("changeCardStatus", eventData);

      // Add local notification immediately
      const actionText = action === "block" ? "blocked" : "unblocked";
      addNotification({
        type: "success",
        title: "Card Status Updated",
        message: `Card ending in ${cardData.cardNumber.slice(
          -4,
        )} has been ${actionText}`,
      });
    }
  };

  const registerBeneficiaryAddedHandler = (handler) => {
    beneficiaryHandlersRef.current = [
      ...beneficiaryHandlersRef.current,
      handler,
    ];

    // Return cleanup function
    return () => {
      beneficiaryHandlersRef.current = beneficiaryHandlersRef.current.filter(
        (h) => h !== handler,
      );
    };
  };

  const registerCardStatusChangedHandler = (handler) => {
    console.log("Registering card status changed handler");
    cardHandlersRef.current = [...cardHandlersRef.current, handler];

    // Return cleanup function
    return () => {
      cardHandlersRef.current = cardHandlersRef.current.filter(
        (h) => h !== handler,
      );
    };
  };

  const registerItemAddedHandler = (handler) => {
    itemHandlersRef.current = [...itemHandlersRef.current, handler];

    // Return cleanup function
    return () => {
      itemHandlersRef.current = itemHandlersRef.current.filter(
        (h) => h !== handler,
      );
    };
  };

  // Function to get pending card action
  const getPendingCardAction = () => {
    return pendingCardAction;
  };

  // Function to clear pending card action
  const clearPendingCardAction = () => {
    setPendingCardAction(null);
  };

  // Test function to simulate cardAction event
  const simulateCardAction = (cardData) => {
    console.log("Simulating cardAction event with:", cardData);

    // Add notification for card status change
    const actionText = cardData.action === "Block" ? "blocking" : "unblocking";
    addNotification({
      type: "success",
      title: `Card ${actionText} request is pending`,
      message: `A request has been initiated`,
    });

    // Set the pending card action to be processed when Cards component loads
    setPendingCardAction(cardData);

    // Navigate to My Cards screen if navigate function is available
    if (navigate) {
      navigate("/cards");
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        removeNotification,
        clearAllNotifications,
        emitBeneficiaryAdd,
        emitItemAdd,
        emitCardStatusChange,
        registerBeneficiaryAddedHandler,
        registerItemAddedHandler,
        registerCardStatusChangedHandler,
        getPendingCardAction,
        clearPendingCardAction,
        simulateCardAction, // Add test function
        socketRef, // Add this line
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
